import type { PaintNode } from './types';

const leaf = (state: number): PaintNode => ({
  splitSides: 0,
  specialSide: 0,
  state: Math.max(0, Math.floor(state)),
  children: null,
});

/** Decode Orca/Bambu TriangleSelector's right-to-left hexadecimal stream. */
export function decodePaintTree(value: string): PaintNode {
  if (!value) return leaf(0);
  const stream = value.trim().split('').reverse();
  let cursor = 0;

  const read = (): PaintNode => {
    if (cursor >= stream.length) return leaf(0);
    const code = Number.parseInt(stream[cursor++], 16);
    if (!Number.isFinite(code)) return leaf(0);
    const splitSides = (code & 3) as 0 | 1 | 2 | 3;
    const upper = ((code >> 2) & 3) as 0 | 1 | 2 | 3;
    if (splitSides === 0) {
      if (upper < 3) return leaf(upper);
      let extended = 0;
      while (cursor < stream.length) {
        const next = Number.parseInt(stream[cursor++], 16);
        if (!Number.isFinite(next)) return leaf(0);
        extended += next;
        if (next !== 15) break;
      }
      return leaf(3 + extended);
    }
    const children = new Array<PaintNode>(splitSides + 1);
    for (let index = children.length - 1; index >= 0; index -= 1) children[index] = read();
    return {
      splitSides,
      specialSide: (upper % 3) as 0 | 1 | 2,
      state: 0,
      children,
    };
  };

  return read();
}

/** Encode a paint tree using Orca's reverse child and reverse nibble ordering. */
export function encodePaintTree(tree: PaintNode): string {
  const stream: number[] = [];
  const write = (node: PaintNode): void => {
    if (!node.children || node.splitSides === 0) {
      const state = Math.max(0, Math.floor(node.state));
      if (state <= 2) {
        stream.push(state << 2);
      } else {
        stream.push(0xC);
        let remaining = state - 3;
        while (remaining >= 15) {
          stream.push(0xF);
          remaining -= 15;
        }
        stream.push(remaining);
      }
      return;
    }
    stream.push(((node.specialSide & 3) << 2) | (node.splitSides & 3));
    for (let index = node.children.length - 1; index >= 0; index -= 1) write(node.children[index]);
  };
  write(simplifyPaintTree(tree));
  return stream.reverse().map((nibble) => nibble.toString(16).toUpperCase()).join('');
}

export function clonePaintTree(tree: PaintNode): PaintNode {
  return {
    splitSides: tree.splitSides,
    specialSide: tree.specialSide,
    state: tree.state,
    children: tree.children?.map(clonePaintTree) ?? null,
  };
}

export function simplifyPaintTree(tree: PaintNode): PaintNode {
  if (!tree.children || tree.splitSides === 0) return leaf(tree.state);
  const children = tree.children.map(simplifyPaintTree);
  const first = children[0];
  if (children.every((child) => !child.children && child.state === first.state)) return leaf(first.state);
  return { ...tree, children };
}

export function nodeAtPath(tree: PaintNode, path: number[]): PaintNode | null {
  let node = tree;
  for (const index of path) {
    if (!node.children?.[index]) return null;
    node = node.children[index];
  }
  return node;
}

export function resolvedState(state: number, inheritedExtruder: number): number {
  return state === 0 ? inheritedExtruder : state;
}

export function stateForExtruder(extruder: number, inheritedExtruder: number): number {
  return extruder === inheritedExtruder ? 0 : extruder;
}
